<template>
    <div class="events-filter-bar container hd-container">
        <div class="events-filter-bar-row">
            <!-- Day pills -->
            <div class="events-filter-bar-days" role="group" aria-label="Filter by day">
                <button
                    v-for="day in days"
                    :key="day.value"
                    type="button"
                    class="events-filter-bar-day"
                    :class="{ 'is-active': modelValue.days.includes(day.value) }"
                    :aria-pressed="modelValue.days.includes(day.value)"
                    @click="toggleIn('days', day.value)"
                >
                    {{ day.label }}
                </button>
            </div>

            <!-- Time and neighborhood inline from $tablet up, where the
                 Filters button used to be (Asana O2-21). Same state as the
                 flyout panel, which still owns them below $tablet. -->
            <div class="events-filter-bar-times" role="group" aria-label="Filter by time of day">
                <button
                    v-for="slot in timeSlots"
                    :key="slot.value"
                    type="button"
                    class="events-filter-bar-day"
                    :class="{ 'is-active': modelValue.times.includes(slot.value) }"
                    :aria-pressed="modelValue.times.includes(slot.value)"
                    @click="toggleIn('times', slot.value)"
                >
                    {{ slot.label }}
                </button>
            </div>

            <div class="events-filter-bar-hood select form-field">
                <!-- No visible label: the option itself reads "All Neighborhoods",
                     which says what the control is. aria-label carries the name
                     for screen readers instead. -->
                <select id="events-hood-inline" aria-label="Filter by neighborhood" v-model="neighborhood">
                    <!-- v-model with a null-valued option, exactly as the flyout
                         panel binds its copy of this control. Vue stores the real
                         value on the option and matches on it, so null selects
                         this one. Binding :value + @change instead left the
                         select's string value with nothing to match and it
                         rendered blank. "All" means "in any neighborhood" — no
                         filter — not "in every neighborhood". -->
                    <option :value="null">All Neighborhoods</option>
                    <optgroup v-for="group in groupedNeighborhoods" :key="group.label" :label="group.label">
                        <option v-for="hood in group.options" :key="hood.id" :value="hood.id">
                            {{ hood.name }}
                        </option>
                    </optgroup>
                </select>
                <SvgsSelectArrow class="select-arrow" />
            </div>

            <p class="events-filter-bar-count">
                <strong>{{ count }}</strong> {{ count === 1 ? 'Event' : 'Events' }}
            </p>
        </div>
    </div>
</template>

<script setup>
    const props = defineProps({
        // { days: string[], times: string[], neighborhood: string|null }
        modelValue: {
            type: Object,
            required: true,
        },
        days: {
            type: Array,
            required: true,
        },
        count: {
            type: Number,
            required: true,
        },
        // Only used by the inline controls above $tablet; the flyout panel gets
        // its own copy of the same list.
        neighborhoods: {
            type: Array,
            required: false,
            default: () => [],
        },
    });

    const emit = defineEmits(['update:modelValue']);

    const set = (patch) => emit('update:modelValue', { ...props.modelValue, ...patch });

    // Days and times are both lists, so a pill adds or removes. Nothing
    // selected and everything selected both mean unfiltered, which is what
    // makes "both days" reachable without a third pill.
    const toggleIn = (key, value) => {
        const current = props.modelValue[key] || [];
        set({ [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value] });
    };

    // Same slots and the same region grouping the flyout panel uses, so the two
    // routes into these filters cannot drift apart.
    const timeSlots = [
        { value: 'morning', label: 'Morning' },
        { value: 'afternoon', label: 'Afternoon' },
    ];

    const groupedNeighborhoods = computed(() => groupNeighborhoods(props.neighborhoods || []));


    // Writable computed so the select can use v-model while this component's
    // state stays a prop — the emit happens in the setter.
    const neighborhood = computed({
        get: () => props.modelValue.neighborhood ?? null,
        set: (value) => set({ neighborhood: value ?? null }),
    });

</script>

<style lang="scss" scoped>
    // One height for every control in the row. The pills derived theirs from
    // padding and line-height (11 + 11 + 1 + 1 + 14 = 38), but a <select> sizes
    // its inner box from the font's own metrics rather than line-height, so the
    // same padding produced 40px and it stood 2px proud of the buttons. Setting
    // the height on both makes them agree, and agree in every engine rather than
    // just the one this was measured in.
    $control-height: 38px;

    .events-filter-bar {
        padding-top: 8px;
        padding-bottom: 16px;
    }

    .events-filter-bar-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
    }

    .events-filter-bar-days {
        display: flex;
        gap: 10px;
    }

    .events-filter-bar-day {
        height: $control-height;
        padding: 0 16px;
        // $gray-common is what .form-field inputs use, so the pills and the
        // search box below them share one border colour.
        border: 1px solid $gray-common;
        background: $white;

        font-family: $title;
        font-size: 14px;
        font-weight: $bold;
        line-height: 1;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: $black;

        // The selected pill reads as a solid black block in the comp, so its
        // border matches its own fill rather than showing a grey ring.
        &.is-active {
            background: $black;
            border-color: $black;
            color: $white;
        }

        &:focus-visible {
            outline: 2px solid $turquoise;
            outline-offset: 2px;
        }
    }





    // Below $tablet these two stay in the flyout, which the filter icon still
    // opens — that split is the ask (Asana O2-21): inline on desktop, behind an
    // icon on a phone.
    .events-filter-bar-times,
    .events-filter-bar-hood {
        display: none;

        @media (min-width: $tablet) {
            display: flex;
        }
    }

    .events-filter-bar-times {
        gap: 10px;
    }

    .events-filter-bar-hood {
        position: relative;
        align-items: center;

        select {
            // Matches the day and time pills beside it rather than the search
            // box below: same border, same uppercase title face, so the row
            // reads as one set of controls.
            appearance: none;
            height: $control-height;
            padding: 0 40px 0 16px;
            border: 1px solid $gray-common;
            background: $white;

            font-family: $title;
            font-size: 14px;
            font-weight: $bold;
            line-height: 1;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            color: $black;

            &:focus-visible {
                outline: 2px solid $turquoise;
                outline-offset: 2px;
            }
        }

        // The closed control keeps the uppercase title face above, so it still
        // matches the day and time pills beside it. Inside the open list the
        // region headings carry that treatment and the names do not, which is
        // what makes the list scannable — 27 uppercase bold names in a column
        // read as one block.
        optgroup {
            font-family: $title;
            font-weight: $bold;
            text-transform: uppercase;
        }

        option {
            font-family: $body;
            font-weight: $regular;
            text-transform: none;
            color: $black;
        }

        .select-arrow {
            position: absolute;
            right: 16px;
            pointer-events: none;
        }
    }

    // Shown at every width (Asana O2-56). This was hidden below tablet on the
    // grounds that the count competed with the day pills for space, but there
    // are only two pills and they leave roughly a third of the row free even at
    // 402px — so the count just read as missing on phones. The row wraps, so if
    // a longer count ever does run out of room it drops to its own line rather
    // than crushing the pills.
    .events-filter-bar-count {
        display: block;
        margin-left: auto;

        font-family: $body;
        font-size: 12px;
        font-weight: $medium;
        letter-spacing: 0.72px;
        text-transform: uppercase;
        color: $gray-darkest;

        strong {
            font-weight: $heavy;
            color: $red;
        }
    }
</style>
